import { EventPathType } from "../types";

import parlors from "./parlors_detail.json";

/**
 * 장례식장 상세 라우트 -> 그 화면의 이벤트 path.
 *
 * 문의 모달은 "어느 장례식장인지" 가 정해진 화면에서만 뜬다.
 * 지역 목록은 여러 곳을 나열하는 화면이라 조문 문의가 걸 번호를 정할 수 없어 대상이 아니다.
 *
 * 포털마다 라우트가 다르고 [id] 계열과 [companyname] 계열 두 벌이 있다.
 * 라우트 문자열은 6개 포털을 통틀어 유일하다.
 * 하단 고정바(StepGuideFooter)가 전 포털 공용이라 여기서 라우트를 판정한다.
 */
export const PARLOR_INQUIRY_EVENT_PATH: Record<string, EventPathType | undefined> = {
  // p_metropolitan
  "/parlor-places/[id]": "prlr_intro",
  "/parlor-places/[id]/price": "prlr_price",
  "/parlor-places/[id]/service": "prlr_service",
  "/parlor-places/[id]/flower": "prlr_flower",
  "/parlor-place/[companyname]": "prlr_intro",
  "/parlor-place/[companyname]/price": "prlr_price",
  "/parlor-place/[companyname]/service": "prlr_service",
  "/parlor-place/[companyname]/flower": "prlr_flower",
  // p_ch
  "/parlor-items/[id]": "prlr_item_intro",
  "/parlor-items/[id]/price": "prlr_item_price",
  "/parlor-items/[id]/service": "prlr_item_service",
  "/parlor-items/[id]/flower": "prlr_item_flower",
  "/parlor-item/[companyname]": "prlr_item_intro",
  "/parlor-item/[companyname]/price": "prlr_item_price",
  "/parlor-item/[companyname]/service": "prlr_item_service",
  "/parlor-item/[companyname]/flower": "prlr_item_flower",
  // p_dggb
  "/funeral-places/[id]": "funeral_place",
  "/funeral-places/[id]/price": "funeral_place_price",
  "/funeral-places/[id]/service": "funeral_place_service",
  "/funeral-places/[id]/flower": "funeral_place_flower",
  "/funeral-place/[companyname]": "funeral_place",
  "/funeral-place/[companyname]/price": "funeral_place_price",
  "/funeral-place/[companyname]/service": "funeral_place_service",
  "/funeral-place/[companyname]/flower": "funeral_place_flower",
  // p_gangwon
  "/gangwon-parlor-places/[id]": "prlr_intro",
  "/gangwon-parlor-places/[id]/price": "prlr_price",
  "/gangwon-parlor-places/[id]/service": "prlr_service",
  "/gangwon-parlor-places/[id]/flower": "prlr_flower",
  "/gangwon-parlor/[companyname]": "prlr_intro",
  "/gangwon-parlor/[companyname]/price": "prlr_price",
  "/gangwon-parlor/[companyname]/service": "prlr_service",
  "/gangwon-parlor/[companyname]/flower": "prlr_flower",
  // p_gyeongsang
  "/funeral-companies/[id]": "funeral_intro",
  "/funeral-companies/[id]/price": "funeral_price",
  "/funeral-companies/[id]/service": "funeral_service",
  "/funeral-companies/[id]/flower": "funeral_flower",
  "/funeral-company/[companyname]": "funeral_intro",
  "/funeral-company/[companyname]/price": "funeral_price",
  "/funeral-company/[companyname]/service": "funeral_service",
  "/funeral-company/[companyname]/flower": "funeral_flower",
  // p_honam
  "/funeral-recommend-items/[id]": "funeral_intro",
  "/funeral-recommend-items/[id]/price": "funeral_price",
  "/funeral-recommend-items/[id]/service": "funeral_service",
  "/funeral-recommend-items/[id]/flower": "funeral_flower",
  "/funeral-recommend-item/[companyname]": "funeral_intro",
  "/funeral-recommend-item/[companyname]/price": "funeral_price",
  "/funeral-recommend-item/[companyname]/service": "funeral_service",
  "/funeral-recommend-item/[companyname]/flower": "funeral_flower",
};

/**
 * 라우트 파라미터로 장례식장 번호를 찾는다.
 *
 * [id] 계열은 숫자, [companyname] 계열은 이름이고 포털마다 장식이 붙는다
 * (ch 는 "대전 대덕구 의 " 접두, gangwon 은 " 정보", gyeongsang 은 " 소개" 접미).
 * 장식 목록을 들고 있으면 문구가 바뀔 때 깨지므로, 숫자 id -> 정확한 이름 ->
 * 파라미터에 포함된 이름 중 가장 긴 것 순서로 찾는다.
 *
 * @param param - router.query.id 또는 router.query.companyname
 */
export const getParlorPhoneByRouteParam = (
  param: string | string[] | undefined
): string | null => {
  if (typeof param !== "string") return null;

  const byId = parlors.find((parlor) => String(parlor.id) === param);
  const exact = byId ?? parlors.find((parlor) => parlor.companyname === param);
  const found =
    exact ??
    parlors
      .filter((parlor) => param.includes(parlor.companyname))
      .sort((a, b) => b.companyname.length - a.companyname.length)[0];

  const telephone = found?.telephone;
  return telephone ? String(telephone).trim() || null : null;
};
